import { actionChannel, all, call, fork, put, take, takeEvery, select, flush } from 'redux-saga/effects'
import actions from './actions'
import crudSagas from '../crud/sagas'
import Api from '../../../api'
import { getAuthToken } from '../../../helpers/tokens'
import {defineGetSaga, defineUpdateSaga} from '../../sagasHelper'
import _ from "lodash";

function * watchAvatarRequests () {
  // 1- Create a channel for request actions
  const requestChan = yield actionChannel(actions.GET_USER_AVATAR_REQUEST)
  while (true) {
    // 2- take from the channel
    const action = yield take(requestChan)
    // 3- Note that we're using a blocking call
    yield call(handleAvatarRequest, action)
  }
}

function * handleAvatarRequest (action) {
  const maps = yield select(state => state.Maps)
  if (maps.avatarProviderId !== action.providerId || (maps.avatarRequests[action.userId] || {}).read !== action.avatarScope) return
  const authToken = yield getAuthToken()
  if (authToken && authToken.token) {
    const { response, error } = yield call(
      Api.Users.getAvatar,
      authToken.token,
      action.providerId,
      action.userId
    )
    const current = yield select(state => state.Maps)
    if (current.avatarScope !== maps.avatarScope || (current.avatarRequests[action.userId] || {}).read !== action.avatarScope) return
    if (!error) {
      yield put(actions.getAvatarSuccess(response || null, action.userId))
    } else {
      yield put(actions.getAvatarError(error, action.userId))
    }
  }
}

export function * getAvatarRequest () {
  yield takeEvery(actions.GET_USER_AVATAR_REQUEST, handleAvatarRequest)
}

export function * updateAvatarRequest () {
  yield takeEvery(actions.UPDATE_USER_AVATAR_REQUEST, function * (
    {
      providerId,
      userId,
      payload,
      avatarScope
    }) {
    const maps = yield select(state => state.Maps)
    if (maps.avatarProviderId !== providerId || (maps.avatarRequests[userId] || {}).write !== avatarScope) return
    const authToken = yield getAuthToken()
    if (authToken && authToken.token) {
      const { response, error } = yield call(
        Api.Users.updateAvatar,
        authToken.token,
        providerId,
        userId,
        payload
      )
      const current = yield select(state => state.Maps)
      if (current.avatarScope !== maps.avatarScope || (current.avatarRequests[userId] || {}).write !== avatarScope) return
      if (!error) {
        yield put(actions.updateAvatarSuccess(response, userId))
        yield put(actions.ensureAllUserAvatars(providerId))
        yield put(actions.getAvatarRequest(providerId, userId))
      } else {
        yield put(actions.updateAvatarError(error, userId))
      }
    }
  })
}

export function * deleteAvatarRequest () {
  yield takeEvery(actions.DELETE_USER_AVATAR_REQUEST, function * (
    {
      providerId,
      userId,
      avatarScope
    }) {
    const maps = yield select(state => state.Maps)
    if (maps.avatarProviderId !== providerId || (maps.avatarRequests[userId] || {}).write !== avatarScope) return
    const authToken = yield getAuthToken()
    if (authToken && authToken.token) {
      const { response, error } = yield call(
        Api.Users.deleteAvatar,
        authToken.token,
        providerId,
        userId
      )
      const current = yield select(state => state.Maps)
      if (current.avatarScope !== maps.avatarScope || (current.avatarRequests[userId] || {}).write !== avatarScope) return
      if (!error) {
        yield put(actions.deleteAvatarSuccess(response, userId))
        yield put(actions.ensureAllUserAvatars(providerId))
        yield put(actions.getAvatarRequest(providerId, userId))
      } else {
        yield put(actions.deleteAvatarError(error, userId))
      }
    }
  })
}

// The request action is emitted only after the cache check, so reducers do not
// mark a skipped ensure as loading. Request identity also survives token retries.
export function * ensureAllUserAvatars () {
  yield takeEvery(actions.ENSURE_ALL_USER_AVATARS, function * ({providerId}) {
    if (!providerId) return
    const cache = yield select(state => state.Users.allAvatars)
    if (cache.providerId !== providerId || cache.loading || cache.loaded) return
    yield put(actions.getAllUserAvatarsRequest({providerId, requestId: {}, previousRequestId: cache.requestId, scope: cache.scope}))
  })
}

export function * getAllUserAvatarsRequest () {
  yield takeEvery(actions.GET_ALL_USER_AVATARS_REQUEST, function * ({ids, onSuccessCallback, onFailureCallback}) {
    const cache = yield select(state => state.Users.allAvatars)
    if (cache.requestId !== ids.requestId) return
    // Keep newer individual results, even if their URL matches the cached URL.
    const updates = yield actionChannel(actions.GET_USER_AVATAR_SUCCESS)
    try {
      const authToken = yield getAuthToken()
      const result = authToken && authToken.token
        ? yield call(Api.Users.getAllUserAvatars, authToken.token, ids.providerId)
        : {error: {message: 'No authentication token'}}
      const current = yield select(state => state.Users.allAvatars)
      if (current.requestId !== ids.requestId) return
      if (!result.error) {
        const individualResults = yield flush(updates)
        const avatars = {...result.response}
        individualResults.forEach(action => { avatars[action.userId] = action.payload })
        yield put(actions.getAllUserAvatarsSuccess(ids, avatars))
        _.isFunction(onSuccessCallback) && onSuccessCallback(avatars)
      } else {
        yield put(actions.getAllUserAvatarsError(ids, result.error))
        _.isFunction(onFailureCallback) && onFailureCallback(result.error)
      }
    } finally {
      updates.close()
    }
  })
}

export function * searchUsersRequest () {
  yield takeEvery(actions.SEARCH_PROVIDERUSERS_REQUEST, function * (action) {
    const { payload, successCallback, errorCallback } = action
    const authToken = yield getAuthToken()
    if (authToken && authToken.token) {
      const { response, error } = yield call(
        Api.Users.searchUsers,
        authToken.token,
        payload.providerId,
        payload)
      if (response) {
        yield put(actions.searchProviderUsersSuccess(response, payload))
        _.isFunction(successCallback) && successCallback(response, payload)
      } else {
        yield put(actions.searchProviderUsersError(error))
        _.isFunction(errorCallback) && errorCallback(error, payload)
      }
    }
  })
}

function * getUserPasswordResetLinkRequest () {
  yield defineGetSaga(actions.GET_USER_PASSWORD_RESET_LINK_REQUEST, Api.Users.getPasswordResetLink, actions.getUserPasswordResetLinkSuccess, actions.getUserPasswordResetLinkError)
}

export function* updateUserPasswordRequest() {
  yield defineUpdateSaga(actions.UPDATE_USER_PASSWORD_REQUEST, Api.Users.resetPasswordForUser, actions.updateUserPasswordSuccess, actions.updateUserPasswordError)
}

export default function * devSaga () {
  yield all([
    crudSagas(actions, Api.Crud, 'providers/:providerId/users', 'userId'),
    fork(watchAvatarRequests),
    fork(updateAvatarRequest),
    fork(deleteAvatarRequest),
    fork(ensureAllUserAvatars),
    fork(getAllUserAvatarsRequest),
    fork(searchUsersRequest),
    fork(getUserPasswordResetLinkRequest),
    fork(updateUserPasswordRequest)
  ])
}
